// aifab - Data page
// Full datasource workflow: upload → profile → feature config → review
import React, { useState, useRef, useEffect } from 'react';
import { useSubmit } from '../hooks/useSubmit';
import { useForm } from '../hooks/useForm';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner, StatusBadge, EmptyState, Modal, ModalBody, ModalFooter, Err, Alert, SectionCard } from '../components/common';
import DocumentsPanel from '../components/data/DocumentsPanel';

const FILE_LIMIT_MB = 2;

export default function DataPage({ projectId, onNavigate }) {
  const { data: datasources, loading, reload } = useApi(
    projectId ? `/datasources?project_id=${projectId}` : null
  );
  const { data: servers } = useApi('/servers');

  // Re-fetch when project switches
  useEffect(() => { if (projectId) reload(); }, [projectId]);

  const [tab,      setTab]      = useState('sources');
  const [showUp,   setShowUp]   = useState(false);    // upload modal
  const [showReg,  setShowReg]  = useState(false);    // large file register
  const [selected, setSelected] = useState(null);     // selected datasource for detail
  const [profiling, setProfiling] = useState({});
  const [err,       setErr]     = useState(null);

  if (!projectId) return (
    <div className="page">
      <div className="page-hd"><div className="page-title">Data</div></div>
      <EmptyState icon="⊞" title="Select a project"
        subtitle="Choose a project from the sidebar to manage its datasets"
        action={<button className="btn btn-primary btn-sm" onClick={() => onNavigate('projects')}>Go to Projects</button>} />
    </div>
  );

  const onlineServers = (servers||[]).filter(s => s.status_code === 'ONLINE');

  const profile = async (ds) => {
    setProfiling(p => ({...p,[ds.id]:true}));
    try {
      await apiFetch(`/datasources/${ds.id}/profile`, { method:'POST' });
      reload();
    } catch(e) { setErr(`Profiling failed: ${e.message}`); }
    finally { setProfiling(p => ({...p,[ds.id]:false})); }
  };

  return (
    <div className="page">
      <div className="page-hd">
        <div>
          <div className="page-sub">Upload datasets, run profiling, configure feature engineering</div>
        </div>
        <div className="flex gap8" data-tour="data-upload">
          {onlineServers.length === 0 && (
            <span className="badge b-amber">No online servers</span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setShowReg(true)}>Register Large File</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowUp(true)}>+ Upload CSV</button>
        </div>
      </div>

      <div className="tabs">
        {['sources',
          'documents',
          selected ? 'profile'   : null,
          selected ? 'features'  : null,
          selected ? 'versions'  : null,
          selected ? 'ds-runs'   : null,
        ].filter(Boolean).map(t => (
          <div key={t} className={`tab${tab===t?' on':''}`} onClick={() => setTab(t)}
            {...(t === 'documents' ? { 'data-tour': 'data-documents' } : {})}
            {...(t === 'features'  ? { 'data-tour': 'data-configure' } : {})}>
            {{ sources:'Datasources', documents:'Documents', profile:'Profile', features:'Feature Config',
               versions:'Versions', 'ds-runs':'Runs' }[t]}
          </div>
        ))}
      </div>

      {/* ── Documents (unstructured ingestion) ───────────────────────────── */}
      {tab === 'documents' && (
        <DocumentsPanel projectId={projectId} servers={onlineServers}
          onDatasourceReady={() => { reload(); setTab('sources'); }} />
      )}

      {/* ── Datasources list ─────────────────────────────────────────────── */}
      {tab === 'sources' && (
        loading ? (
          <div className="flex" style={{justifyContent:'center',padding:48}}><Spinner size={28}/></div>
        ) : (datasources||[]).length === 0 ? (
          <EmptyState icon="⊞" title="No datasets yet"
            subtitle={`Upload a CSV (≤${FILE_LIMIT_MB}MB) or register a large file path`}
            action={<button className="btn btn-primary btn-sm" onClick={() => setShowUp(true)}>+ Upload CSV</button>} />
        ) : (
          <div className="card">
            <div className="tbl-wrap">
              <table data-tour="data-profile">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>File</th>
                    <th>Rows</th>
                    <th>Columns</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(datasources||[]).map(d => (
                    <tr key={d.id} style={{ cursor:'pointer' }}
                      onClick={() => { setSelected(d); setTab('profile'); }}>
                      <td>
                        <div className="bold">{d.name}</div>
                        <div className="mono xs c-3">{d.id.slice(0,8)}…</div>
                      </td>
                      <td className="td-mono td-muted">{d.file_name || '-'}</td>
                      <td className="td-mono">{d.total_rows?.toLocaleString() ?? '-'}</td>
                      <td className="td-mono">{d.total_columns ?? '-'}</td>
                      <td><StatusBadge code={d.status} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="flex gap6">
                          {d.status === 'PENDING' && (
                            <button className="btn btn-ghost btn-xs" onClick={() => profile(d)}
                              disabled={profiling[d.id]}>
                              {profiling[d.id] ? <Spinner size={10}/> : '⚡ Profile'}
                            </button>
                          )}
                          {d.status === 'PROFILED' && (
                            <>
                              <button className="btn btn-ghost btn-xs" onClick={() => profile(d)}
                                disabled={profiling[d.id]} title="Re-run profiling with latest data">
                                {profiling[d.id] ? <Spinner size={10}/> : '↻ Re-profile'}
                              </button>
                              <button className="btn btn-success btn-xs"
                                onClick={() => { setSelected(d); setTab('features'); }}>
                                Configure →
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── Selected-datasource context strip: Profile/Features/Versions/Runs
           all operate on this selection - without it the screen is ambiguous */}
      {['profile', 'features', 'versions', 'ds-runs'].includes(tab) && selected && (
        <div style={{ display:'flex', alignItems:'center', gap:10, margin:'0 0 14px',
          padding:'8px 14px', background:'var(--surface-alt, #F7F4F0)',
          border:'1px solid #E5E0DA', borderRadius:8, fontSize:13 }}>
          <span style={{ color:'#6B635C' }}>Datasource</span>
          <strong>{selected.name}</strong>
          <span className="mono" style={{ fontSize:11, color:'#A39A90' }}>
            v{selected.active_version || 1}{selected.total_rows ? ` · ${Number(selected.total_rows).toLocaleString()} rows` : ''}
          </span>
          <button className="btn btn-ghost btn-xs" style={{ marginLeft:'auto' }}
            onClick={() => setTab('sources')}>Change</button>
        </div>
      )}

      {/* ── Profile detail ───────────────────────────────────────────────── */}
      {tab === 'profile' && selected && (
        <ProfileTab ds={selected} projectId={projectId} onDone={() => { reload(); setTab('features'); }} />
      )}

      {/* ── Feature config ───────────────────────────────────────────────── */}
      {tab === 'versions' && selected && (
        <DatasourceVersionsTab ds={selected} />
      )}
      {tab === 'ds-runs' && selected && (
        <DatasourceRunsTab ds={selected} />
      )}
      {tab === 'features' && selected && (
        <FeatureTab ds={selected} projectId={projectId} onDone={reload} />
      )}

      {/* ── Upload modal ─────────────────────────────────────────────────── */}
      {showUp && (
        <UploadModal
          projectId={projectId}
          servers={onlineServers}
          onClose={() => setShowUp(false)}
          onDone={() => { reload(); setShowUp(false); }}
        />
      )}

      {/* ── Register large file modal ─────────────────────────────────────── */}
      {showReg && (
        <RegisterModal
          projectId={projectId}
          servers={onlineServers}
          onClose={() => setShowReg(false)}
          onDone={() => { reload(); setShowReg(false); }}
        />
      )}
    </div>
  );
}

/* ── Upload Modal ──────────────────────────────────────────────────────────── */
function UploadModal({ projectId, servers, onClose, onDone }) {
  const [file,     setFile]    = useState(null);
  const [name,     setName]    = useState('');
  const [serverId, setServerId] = useState(servers[0]?.id || '');
  const [desc,     setDesc]    = useState('');
  const [uploading, setUploading] = useState(false);
  const [drag,     setDrag]    = useState(false);
  const [err,      setErr]     = useState(null);
  const inputRef = useRef();

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    if (!name) setName(f.name.replace(/\.csv$/i,''));
  };

  const upload = async () => {
    if (!file)     { setErr('Select a CSV file'); return; }
    if (!name)     { setErr('Name is required');  return; }
    if (!serverId) { setErr('Select a server');   return; }
    const mb = file.size / 1024 / 1024;
    if (mb > FILE_LIMIT_MB) {
      setErr(`File is ${mb.toFixed(1)} MB - exceeds ${FILE_LIMIT_MB} MB limit. Use "Register Large File" instead.`);
      return;
    }
    setUploading(true); setErr(null);
    const fd = new FormData();
    fd.append('project_id', projectId);
    fd.append('server_id',  serverId);
    fd.append('name',       name);
    fd.append('description', desc);
    fd.append('file',       file);
    try {
      await apiFetch('/datasources/upload', { method:'POST', body:fd, raw:true });
      onDone();
    } catch(e) { setErr(e.message); }
    finally { setUploading(false); }
  };

  return (
    <Modal title="Upload Dataset" subtitle={`CSV files up to ${FILE_LIMIT_MB} MB`} onClose={onClose}>
      <ModalBody>
        <Err msg={err} />

        {/* Drop zone */}
        <div
          className={`drop-zone mb16${drag?' drag':''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); onFile(e.dataTransfer.files[0]); }}
        >
          <input ref={inputRef} type="file" accept=".csv"
            onChange={e => onFile(e.target.files[0])} />
          {file ? (
            <div>
              <div style={{ fontSize:14, fontWeight:600, color:'#0F172A' }}>{file.name}</div>
              <div className="xs c-3 mt8">{(file.size/1024/1024).toFixed(2)} MB</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize:24, marginBottom:8, opacity:.4 }}>⊞</div>
              <div style={{ fontSize:13, color:'#334155', fontWeight:500 }}>Drop CSV here or click to browse</div>
              <div className="xs c-3 mt8">Maximum {FILE_LIMIT_MB} MB · CSV only</div>
            </div>
          )}
        </div>

        <div className="form-row form-row-2">
          <div className="form-grp">
            <label className="form-lbl">Dataset Name *</label>
            <input className="form-inp" value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. ITSM Tickets 2024" />
          </div>
          <div className="form-grp">
            <label className="form-lbl">Execution Server *</label>
            <select className="form-inp" value={serverId} onChange={e => setServerId(e.target.value)}>
              <option value="">- Select -</option>
              {servers.map(s => <option key={s.id} value={s.id}>{s.server_name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-grp">
          <label className="form-lbl">Description</label>
          <input className="form-inp" value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="Optional description" />
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={upload} disabled={uploading || !file}>
          {uploading ? <><Spinner size={12}/> Uploading…</> : 'Upload & Register'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ── Register Large File Modal ─────────────────────────────────────────────── */
function RegisterModal({ projectId, servers, onClose, onDone }) {
  const [form, setForm]   = useState({ name:'', file_name:'', server_id: servers[0]?.id||'', description:'' });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err,    setErr]   = useState(null);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const register = async () => {
    if (!form.name || !form.file_name || !form.server_id) { setErr('All fields required'); return; }
    setSaving(true); setErr(null);
    try {
      const r = await apiFetch('/datasources/register', { method:'POST', body:JSON.stringify({ ...form, project_id:projectId }) });
      setResult(r);
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Register Large Dataset" subtitle="For files larger than 2 MB" onClose={onClose}>
      <ModalBody>
        {result ? (
          <>
            <Alert type="ok">Dataset registered. UUID: <span className="mono">{result.datasource_id.slice(0,8)}…</span></Alert>
            <div style={{ background:'#E4EAF4', borderRadius:'8px', padding:16 }}>
              <div className="xs c-3 mb8" style={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em' }}>
                Place your file at this path on the execution server:
              </div>
              <div className="mono sm" style={{ wordBreak:'break-all', color:'#0F172A' }}>
                {result.remote_path}
              </div>
              <div className="sep" />
              <div className="xs c-3 mb4" style={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em' }}>SCP command:</div>
              <div className="mono xs" style={{ background:'#1E293B', color:'#475569', padding:'8px 12px', borderRadius:'4px', wordBreak:'break-all', fontFamily:'var(--f-mono)', fontSize:12 }}>
                scp your_file.csv {servers.find(s=>s.id===form.server_id)?.ssh_user||'ubuntu'}@
                {servers.find(s=>s.id===form.server_id)?.host}:{result.remote_path}
              </div>
              <div className="xs c-3 mt12">After placing the file, trigger profiling from the Datasources table.</div>
            </div>
          </>
        ) : (
          <>
            <Err msg={err} />
            <div className="form-row form-row-2">
              <div className="form-grp">
                <label className="form-lbl">Dataset Name *</label>
                <input className="form-inp" value={form.name} onChange={e => set('name',e.target.value)}
                  placeholder="e.g. Maintenance Logs 2024" />
              </div>
              <div className="form-grp">
                <label className="form-lbl">Original Filename *</label>
                <input className="form-inp" value={form.file_name} onChange={e => set('file_name',e.target.value)}
                  placeholder="maintenance_logs.csv" />
              </div>
            </div>
            <div className="form-grp">
              <label className="form-lbl">Target Server *</label>
              <select className="form-inp" value={form.server_id} onChange={e => set('server_id',e.target.value)}>
                <option value="">- Select -</option>
                {servers.map(s => <option key={s.id} value={s.id}>{s.server_name} ({s.host})</option>)}
              </select>
              <div className="form-hint">The UUID-named path will be generated on this server.</div>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {result ? (
          <button className="btn btn-primary btn-sm" onClick={onDone}>Done</button>
        ) : (
          <>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={register} disabled={saving}>
              {saving ? <Spinner size={12}/> : 'Register & Get Path'}
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

/* ── Profile Tab ───────────────────────────────────────────────────────────── */
function ProfileTab({ ds, projectId, onDone }) {
  const { data: detail, loading, reload } = useApi(`/datasources/${ds.id}`);
  const [reprofiling, setReprofiling] = useState(false);

  const reprofile = async () => {
    setReprofiling(true);
    try {
      await apiFetch(`/datasources/${ds.id}/profile`, { method: 'POST' });
      reload();
    } catch (e) { alert(e.message); }
    finally { setReprofiling(false); }
  };

  if (loading) return <div className="flex" style={{justifyContent:'center',padding:48}}><Spinner/></div>;
  if (!detail)  return <div className="alert alert-err">Could not load datasource</div>;

  const cols = detail.columns || [];

  return (
    <div>
      {/* Summary stats */}
      <div className="flex-btw mb20">
        <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', flex:1, marginRight:12 }}>
          {[
            ['Rows',    detail.total_rows?.toLocaleString() ?? '-'],
            ['Columns', detail.total_columns ?? '-'],
            ['Status',  <StatusBadge code={detail.status}/>],
            ['Size',    detail.file_size_bytes ? `${(detail.file_size_bytes/1024/1024).toFixed(2)} MB` : '-'],
          ].map(([l,v]) => (
            <div key={l} className="stat-card">
              <div className="stat-lbl">{l}</div>
              <div className="stat-val" style={{fontSize:20}}>{v}</div>
            </div>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={reprofile} disabled={reprofiling}
          title="Re-run profiling - updates row count and column statistics">
          {reprofiling ? <><Spinner size={12}/> Profiling…</> : '↻ Re-profile'}
        </button>
      </div>

      {/* Observations */}
      {(detail.observations || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B',
            textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
            Observations
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {detail.observations.map((obs, i) => {
              const colors = {
                warning: { bg: 'rgba(239,68,68,.07)',  border: 'rgba(239,68,68,.25)',  icon: '⚠', tx: '#f87171' },
                info:    { bg: 'rgba(59,130,246,.07)', border: 'rgba(59,130,246,.2)',  icon: 'ℹ', tx: '#60a5fa' },
                tip:     { bg: 'rgba(52,211,153,.07)', border: 'rgba(52,211,153,.2)',  icon: '💡', tx: '#34d399' },
              };
              const c = colors[obs.level] || colors.info;
              return (
                <div key={i} style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  background: c.bg, border: `1px solid ${c.border}`,
                  borderRadius: 4, padding: '8px 12px',
                }}>
                  <span style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }}>{c.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {obs.column && (
                      <span style={{ fontFamily: 'var(--f-mono)', fontSize: 10,
                        color: c.tx, marginRight: 8, fontWeight: 700 }}>
                        {obs.column}
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: '#334155', lineHeight: 1.5 }}>
                      {obs.message}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Column grid */}
      {cols.length > 0 && (
        <div className="card">
          <div className="card-hd">
            <div className="card-title">Column Profiles</div>
            <span className="badge b-gray">{cols.length} columns</span>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Column</th>
                  <th>Type</th>
                  <th>Nulls</th>
                  <th>Unique</th>
                  <th>Target?</th>
                  <th>Samples</th>
                </tr>
              </thead>
              <tbody>
                {cols.map((c,i) => (
                  <tr key={c.id}>
                    <td className="td-mono td-muted">{i+1}</td>
                    <td className="bold">{c.name}</td>
                    <td><span className="badge b-gray">{c.inferred_type}</span></td>
                    <td className="td-mono">
                      {c.null_count != null ? (
                        <span className={c.null_count > 0 ? 'c-am' : 'c-3'}>{c.null_count}</span>
                      ) : '-'}
                    </td>
                    <td className="td-mono">{c.unique_count?.toLocaleString() ?? '-'}</td>
                    <td>{c.is_candidate_target && <span className="badge b-or">Target</span>}</td>
                    <td className="td-muted xs mono">
                      {(c.sample_values||[]).slice(0,3).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail.status === 'PROFILED' && (
        <div style={{ marginTop:16, textAlign:'right' }}>
          <button className="btn btn-success btn-sm" onClick={onDone}>
            Configure Features →
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Feature Config Tab ────────────────────────────────────────────────────── */
function FeatureTab({ ds, projectId, onDone }) {
  const { data: configs, loading, reload } = useApi(`/datasources/${ds.id}/feature-configs`);
  const [creating,    setCreating]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [forkingFrom, setForkingFrom] = useState(null);  // fc object to fork
  const [forkReason,  setForkReason]  = useState('');
  const [forkSaving,  setForkSaving]  = useState(false);
  const [forkErr,     setForkErr]     = useState(null);

  const submitFork = async () => {
    if (!forkReason.trim()) { setForkErr('Reason is required'); return; }
    setForkSaving(true); setForkErr(null);
    try {
      await apiFetch(
        `/datasources/${ds.id}/feature-configs/${forkingFrom.id}/fork?fork_reason=${encodeURIComponent(forkReason.trim())}`,
        { method: 'POST' }
      );
      setForkingFrom(null); setForkReason('');
      reload();
    } catch(e) { setForkErr(e.message); }
    finally { setForkSaving(false); }
  };   // fc object being edited
  const [err, setErr] = useState(null);

  const createConfig = async () => {
    setCreating(true); setErr(null);
    try {
      await apiFetch(`/datasources/${ds.id}/feature-config`, { method:'POST' });
      reload();
    } catch(e) { setErr(e.message); }
    finally { setCreating(false); }
  };

  if (loading) return <div className="flex" style={{justifyContent:'center',padding:48}}><Spinner/></div>;

  return (
    <div>
      <Err msg={err} />
      {(configs||[]).length === 0 ? (
        <EmptyState icon="◈" title="No feature configs"
          subtitle="Auto-generate a feature config from the profiling results"
          action={
            <button className="btn btn-primary btn-sm" onClick={createConfig} disabled={creating}>
              {creating ? <Spinner size={12}/> : '⚡ Auto-Generate Config'}
            </button>
          } />
      ) : (
        <>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <button className="btn btn-ghost btn-sm" onClick={createConfig} disabled={creating}>
              {creating ? <Spinner size={12}/> : '+ New Config'}
            </button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {(() => {
              const all = configs || [];
              const masters = all.filter(fc => !fc.base_config_id);
              const forks   = all.filter(fc =>  fc.base_config_id);
              const forksByBase = forks.reduce((acc, fc) => {
                (acc[fc.base_config_id] = acc[fc.base_config_id] || []).push(fc);
                return acc;
              }, {});
              return masters.map(master => (
                <div key={master.id}>
                  <FeatureConfigCard fc={master} ds={ds}
                    onEdit={() => setEditing(master)}
                    onFork={() => { setForkingFrom(master); setForkReason(''); setForkErr(null); }}
                    onReload={reload} />
                  {(forksByBase[master.id] || []).sort((a,b) => a.fork_version - b.fork_version).map(fork => (
                    <div key={fork.id} style={{
                      marginLeft:24, marginTop:6,
                      borderLeft:'2px solid rgba(139,92,246,.25)',
                      paddingLeft:12,
                    }}>
                      <FeatureConfigCard fc={fork} ds={ds}
                        onEdit={() => setEditing(fork)}
                        onFork={() => { setForkingFrom(master); setForkReason(''); setForkErr(null); }}
                        onReload={reload} />
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        </>
      )}

      {forkingFrom && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:999 }}
          onClick={() => setForkingFrom(null)}>
          <div style={{ background:'#E4EAF4', border:'1px solid var(--bd)', borderRadius:10,
            padding:24, width:440, maxWidth:'90vw' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>
              Fork: {forkingFrom.config_name}
            </div>
            <div style={{ fontSize:11, color:'#64748B', marginBottom:16 }}>
              Creates a versioned copy at project level. The original config is unchanged.
              Both remain available for any run.
            </div>
            {forkErr && <div style={{ color:'var(--red)', fontSize:11, marginBottom:8 }}>{forkErr}</div>}
            <div className="form-grp">
              <label className="form-lbl">Reason for fork (required)</label>
              <textarea className="form-inp" rows={3} value={forkReason}
                onChange={e => setForkReason(e.target.value)}
                placeholder="What are you changing and why? e.g. Removing low-variance columns for the churn usecase" />
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setForkingFrom(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm"
                disabled={forkSaving || !forkReason.trim()} onClick={submitFork}>
                {forkSaving ? <Spinner size={12}/> : '⑂ Create Fork'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <FeatureConfigEditModal
          fc={editing}
          ds={ds}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

/* ── Feature Config Card ───────────────────────────────────────────────────── */
function FeatureConfigCard({ fc, ds, onEdit, onFork, onReload }) {
  const [reviewing, setReviewing] = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [err, setErr] = useState(null);

  const markReviewed = async () => {
    setReviewing(true); setErr(null);
    try {
      await apiFetch(`/datasources/${ds.id}/feature-config/${fc.id}/review`,
        { method:'PATCH', body:JSON.stringify({ approved:true }) });
      onReload();
    } catch(e) { setErr(e.message); }
    finally { setReviewing(false); }
  };

  const deleteConfig = async () => {
    if (!window.confirm(`Delete "${fc.config_name}"? This cannot be undone.`)) return;
    setDeleting(true); setErr(null);
    try {
      await apiFetch(`/datasources/${ds.id}/feature-configs/${fc.id}`, { method:'DELETE' });
      onReload();
    } catch(e) { setErr(e.message); setDeleting(false); }
  };

  return (
    <div className="card">
      {err && <div style={{ padding:'8px 18px 0' }}><Err msg={err}/></div>}
      <div style={{ padding:'16px 18px', display:'flex', alignItems:'flex-start', gap:14 }}>

        {/* Left - info */}
        <div style={{ flex:1 }}>
          <div className="bold" style={{ marginBottom:4, display:'flex', alignItems:'center', gap:8 }}>
            {fc.base_config_id && (
              <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3,
                background:'rgba(139,92,246,.1)', color:'#a78bfa', fontWeight:700 }}>
                fork v{fc.fork_version}
              </span>
            )}
            {fc.config_name}
            {fc.config_purpose && (
              <span style={{
                fontSize:10, fontWeight:600, padding:'1px 7px', borderRadius:4,
                background: {
                  CLASSIFICATION: 'rgba(232,74,0,0.08)',
                  CORPUS:         'rgba(22,163,74,0.08)',
                  REGRESSION:     'rgba(37,99,235,0.08)',
                  NER:            'rgba(124,58,237,0.08)',
                  CUSTOM:         'rgba(107,114,128,0.08)',
                }[fc.config_purpose] || 'rgba(107,114,128,0.08)',
                color: {
                  CLASSIFICATION: 'var(--brand-orange)',
                  CORPUS:         'var(--success)',
                  REGRESSION:     'var(--info)',
                  NER:            '#7c3aed',
                  CUSTOM:         'var(--text-secondary)',
                }[fc.config_purpose] || 'var(--text-secondary)',
              }}>
                {{
                  CLASSIFICATION: 'Classification',
                  CORPUS:         'Domain Pre-training',
                  REGRESSION:     'Regression',
                  NER:            'NER',
                  CUSTOM:         'Custom',
                }[fc.config_purpose] || fc.config_purpose}
              </span>
            )}
          </div>
          <div className="xs" style={{ color:'#64748B', marginBottom:6 }}>
            Dataset: <span className="mono" style={{ color:'#334155' }}>{ds.datasource_name || ds.file_name || '-'}</span>
          </div>
          <div className="flex gap12" style={{ flexWrap:'wrap' }}>
            {fc.target_column && ['CLASSIFICATION','REGRESSION',null,undefined,''].includes(fc.config_purpose) && (
              <div className="xs" style={{ color:'#64748B' }}>
                <span style={{ color:'#64748B', marginRight:4 }}>Target</span>
                <span className="mono" style={{ color:'#334155', background:'#E4EAF4', padding:'1px 6px', borderRadius:3 }}>
                  {fc.target_column}
                </span>
              </div>
            )}
            {fc.train_split != null && (
              <div className="xs" style={{ color:'#64748B' }}>
                Split{' '}
                <span style={{ color:'#334155' }}>
                  {Math.round(fc.train_split*100)}/{Math.round(fc.val_split*100)}/{Math.round(fc.test_split*100)}
                </span>
              </div>
            )}
            {fc.stratify != null && (
              <div className="xs" style={{ color:'#64748B' }}>
                {fc.stratify ? '✓ Stratified' : 'Not stratified'}
              </div>
            )}
          </div>
          {fc.fork_reason && (
            <div className="xs" style={{ color:'#64748B', fontStyle:'italic', marginTop:4 }}>
              "{fc.fork_reason}"
            </div>
          )}
          <div className="xs c-3 mt6 mono">{fc.id?.slice(0,8)}… · {fc.created_at?.slice(0,10)}</div>
        </div>

        {/* Right - actions */}
        <div className="flex gap8" style={{ alignItems:'center', flexShrink:0 }}>
              <>
              {!fc.user_reviewed && (
                <button className="btn btn-ghost btn-sm" onClick={onEdit}>✎ Edit</button>
              )}
              {fc.user_reviewed
                ? <span className="badge b-green">✓ Reviewed</span>
                : <button className="btn btn-success btn-sm" onClick={markReviewed} disabled={reviewing}>
                    {reviewing ? <Spinner size={12}/> : '✓ Approve'}
                  </button>
              }
              {fc.user_reviewed && (
                <button className="btn btn-ghost btn-sm" onClick={onEdit}>✎ Edit</button>
              )}
              <button className="btn btn-ghost btn-sm" onClick={onFork}
                title="Fork this config to create a versioned variant">
                ⑂
              </button>
              <button className="btn btn-ghost btn-sm" onClick={deleteConfig}
                disabled={deleting} style={{ color:'var(--red)' }}
                title="Delete this feature config">
                {deleting ? <Spinner size={12}/> : '✕'}
              </button>
            </>
        </div>
      </div>

      {showDetail && (
        <div style={{ padding:'0 18px 16px', borderTop:'1px solid var(--bd)' }}>
          <div className="xs c-sl mb8" style={{ fontWeight:600, letterSpacing:'.07em',
            textTransform:'uppercase', marginTop:12 }}>Column Roles</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {(() => {
              const rawFc = fc.feature_columns || {};
              const keys  = Object.keys(rawFc);
              const isPos = keys.length > 0 && keys.every(k => /^\d+$/.test(k));
              const roleMap = {};
              if (isPos) {
                Object.values(rawFc).forEach(col => {
                  const cfg = (fc.encoding_config || {})[col];
                  const r   = typeof cfg === 'object' ? cfg.role : cfg;
                  roleMap[col] = ['text','feature','ignore','id','target'].includes(r) ? r : 'feature';
                });
              } else {
                Object.entries(rawFc).forEach(([col, r]) => { roleMap[col] = r; });
              }
              return Object.entries(roleMap).map(([col, role]) => {
                const color = role === 'text'    ? '#60a5fa'
                            : role === 'feature' ? 'var(--or)'
                            : role === 'target'  ? '#34d399'
                            : '#64748B';
                return (
                  <div key={col} style={{
                    padding:'3px 8px', borderRadius:4, fontSize:11,
                    background:'#F1F5FB', border:'1px solid var(--bd)',
                    opacity: (role === 'ignore' || role === 'id') ? 0.5 : 1,
                  }}>
                    <span className="mono" style={{ color:'#334155' }}>{col}</span>
                    <span style={{ marginLeft:5, color, fontWeight:600, fontSize:9,
                      textTransform:'uppercase' }}>{role}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Feature Config Edit Modal ─────────────────────────────────────────────── */
function FeatureConfigEditModal({ fc, ds, onClose, onDone }) {
  const { data: detail, loading: detailLoading } = useApi(
    `/datasources/${ds.id}/feature-configs/${fc.id}`
  );
  const { data: dsDetail } = useApi(`/datasources/${ds.id}`);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr]   = useState(null);
  const [tab, setTab]   = useState('core');  // core | columns | encoding

  // Re-populate form every time detail loads or changes (ensures edits reflect on re-open)
  useEffect(() => {
    if (detail) {
      // Normalise feature_columns to {col_name: role} format.
      // The profiler stores it as positional {"0": "col_name"} - convert it.
      // encoding_config has the actual roles as {"col_name": {role: "text"|"ignore"...}}
      const rawEnc   = detail.encoding_config || {};
      const rawRoles = detail.feature_columns || {};

      // Detect positional format: keys are numeric strings "0", "1", "2"...
      const isPositional = Object.keys(rawRoles).length > 0 &&
        Object.keys(rawRoles).every(k => /^\d+$/.test(k));

      let normalisedRoles = {};
      if (isPositional) {
        // Convert positional to {col_name: role} using encoding_config roles if available
        Object.values(rawRoles).forEach(colName => {
          const encCfg = rawEnc[colName];
          const role   = (typeof encCfg === 'object' && encCfg?.role)
            ? encCfg.role
            : 'feature';
          // Map profiler role names to standard roles
          const standardRole = ['text','feature','ignore','id','target'].includes(role)
            ? role
            : 'feature';
          normalisedRoles[colName] = standardRole;
        });
      } else {
        // Already in {col_name: role} format
        normalisedRoles = rawRoles;
      }

      const flatEnc = {};
      Object.entries(rawEnc).forEach(([col, cfg]) => {
        const userRole = normalisedRoles[col];
        if (userRole === 'ignore' || userRole === 'id') return;
        if (col === detail.target_column) return;
        const encVal = typeof cfg === 'object' && cfg !== null ? cfg.encoding : cfg;
        if (encVal) flatEnc[col] = encVal;
      });
      setForm({
        config_name:    detail.config_name    || '',
        config_purpose: detail.config_purpose  || 'CLASSIFICATION',
        target_column:  detail.target_column  || '',
        entity_column:  detail.entity_column  || '',
        train_split:    detail.train_split    ?? 0.8,
        val_split:      detail.val_split      ?? 0.1,
        test_split:     detail.test_split     ?? 0.1,
        stratify:       detail.stratify       ?? true,
        feature_columns: normalisedRoles,
        encoding_config: flatEnc,
      });
    }
  }, [detail]);

  const columns = dsDetail?.columns || [];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const updateSplit = (key, raw) => {
    const val = parseFloat(raw) || 0;
    setForm(f => {
      const next = { ...f, [key]: val };
      // Auto-adjust: keep the other two proportional
      const others = ['train_split','val_split','test_split'].filter(k2 => k2 !== key);
      const remaining = Math.max(0, 1 - val);
      const total = (f[others[0]] || 0) + (f[others[1]] || 0) || 1;
      next[others[0]] = Math.round(f[others[0]] / total * remaining * 100) / 100;
      next[others[1]] = Math.round((remaining - next[others[0]]) * 100) / 100;
      return next;
    });
  };

  const save = async (andApprove = false) => {
    const purpose  = form.config_purpose || 'CLASSIFICATION';
    const hasTarget = !!form.target_column;
    const textCols  = Object.entries(form.feature_columns || {}).filter(([,v]) => v === 'text').length;
    const needsTarget = ['CLASSIFICATION', 'REGRESSION'].includes(purpose);

    if (needsTarget && !hasTarget) {
      setErr(`A target column is required for ${purpose} configs.`);
      return;
    }
    if (purpose === 'NER' && !form.entity_column) {
      setErr('An entity column (IOB-tagged) is required for NER configs.');
      return;
    }
    if (!needsTarget && purpose !== 'CUSTOM' && textCols === 0) {
      setErr('At least one column must be set as text input.');
      return;
    }
    const sum = (form.train_split + form.val_split + form.test_split);
    if (Math.abs(sum - 1.0) > 0.02) { setErr(`Splits must sum to 1.0 (currently ${sum.toFixed(2)})`); return; }
    setSaving(true); setErr(null);
    try {
      await apiFetch(`/datasources/${ds.id}/feature-configs/${fc.id}`,
        { method:'PATCH', body:JSON.stringify({
          config_name:    form.config_name,
          config_purpose: purpose,
          // Clear target_column for purposes that don't use it
          target_column:  ['CLASSIFICATION','REGRESSION'].includes(purpose) ? (form.target_column || null) : null,
          entity_column:  form.entity_column || null,
          feature_columns: form.feature_columns,
          train_split:    form.train_split,
          val_split:      form.val_split,
          test_split:     form.test_split,
          stratify:       form.stratify,
          encoding_config: form.encoding_config,
        })});
      if (andApprove) {
        await apiFetch(`/datasources/${ds.id}/feature-config/${fc.id}/review`,
          { method:'PATCH', body:JSON.stringify({
            approved:       true,
            config_purpose: purpose,
            // Explicitly null for purposes that have no target - never leave stale value
            target_column:  ['CLASSIFICATION','REGRESSION'].includes(purpose)
              ? (form.target_column || null)
              : null,
          }) });
      }
      onDone();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const splitPct = form ? [
    { label:'Train', key:'train_split', color:'var(--or)' },
    { label:'Val',   key:'val_split',   color:'var(--blue)' },
    { label:'Test',  key:'test_split',  color:'var(--green)' },
  ] : [];

  return (
    <Modal
      title="Edit Feature Config"
      subtitle='Review and adjust before approving for training'
      onClose={onClose}
      wide
    >
      <ModalBody>
        {detailLoading || !form ? (
          <div className="flex" style={{justifyContent:'center',padding:48}}><Spinner/></div>
        ) : (
          <>
            <Err msg={err} />

            {/* Inner tabs */}
            <div className="tabs" style={{ marginBottom:20 }}>
              {[['core','Core Settings'],['columns','Columns'],['encoding','Encoding']].map(([t,l]) => (
                <div key={t} className={`tab${tab===t?' on':''}`} onClick={() => setTab(t)}>{l}</div>
              ))}
            </div>

            {/* ── Core Settings ── */}
            {tab === 'core' && (
              <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

                {/* Purpose selector - first and most important */}
                <div className="form-grp">
                  <label className="form-lbl">Config Purpose</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:6 }}>
                    {[
                      { code:'CLASSIFICATION', label:'Classification', hint:'Target column required' },
                      { code:'CORPUS',         label:'Domain Pre-training', hint:'No target - text only' },
                      { code:'REGRESSION',     label:'Regression',     hint:'Numeric target required' },
                      { code:'NER',            label:'NER',            hint:'Text + entity columns' },
                      { code:'CUSTOM',         label:'Custom',         hint:'No enforcement' },
                    ].map(p => (
                      <div
                        key={p.code}
                        onClick={() => !fc.user_reviewed && set('config_purpose', p.code)}
                        style={{
                          padding:'8px 10px', borderRadius:'var(--radius-sm)', textAlign:'center',
                          cursor: fc.user_reviewed || fc.is_locked ? 'default' : 'pointer',
                          border:`2px solid ${form.config_purpose===p.code ? 'var(--brand-orange)' : 'var(--border)'}`,
                          background: form.config_purpose===p.code ? 'rgba(232,74,0,0.04)' : 'var(--bg-elevated)',
                          opacity: fc.user_reviewed || fc.is_locked ? 0.7 : 1,
                        }}
                      >
                        <div style={{ fontSize:11, fontWeight:600, color: form.config_purpose===p.code ? 'var(--brand-orange)' : 'var(--text-primary)' }}>
                          {p.label}
                        </div>
                        <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>{p.hint}</div>
                      </div>
                    ))}
                  </div>
                  {fc.user_reviewed && (
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>
                      Purpose is locked after approval. Delete and recreate to change it.
                    </div>
                  )}
                </div>

                <div className="form-grp">
                  <label className="form-lbl">Config Name</label>
                  <input className="form-inp" value={form.config_name}
                    onChange={e => set('config_name', e.target.value)}
                    disabled={fc.is_locked} />
                </div>

                {/* Target column - shown for purposes that need it */}
                {['CLASSIFICATION','REGRESSION'].includes(form.config_purpose) && (
                  <div className="form-grp">
                    <label className="form-lbl">
                      Target Column
                      {form.config_purpose === 'REGRESSION' && (
                        <span style={{ marginLeft:6, fontSize:10, color:'var(--text-muted)', fontWeight:400 }}>
                          (must be numeric)
                        </span>
                      )}
                    </label>
                    <select className="form-inp" value={form.target_column}
                      onChange={e => set('target_column', e.target.value)}
                      disabled={fc.is_locked}>
                      <option value="">- Select label column -</option>
                      {columns.map(c => (
                        <option key={c.id} value={c.name}>
                          {c.name}
                          {c.is_candidate_target ? ' ★' : ''}
                          {c.inferred_type ? ` (${c.inferred_type})` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="form-hint">★ = candidate target detected by profiler</div>
                  </div>
                )}

                {/* Corpus hint */}
                {form.config_purpose === 'CORPUS' && (
                  <div style={{
                    padding:'10px 12px', borderRadius:'var(--radius-sm)',
                    background:'rgba(232,74,0,0.04)', border:'1px solid rgba(232,74,0,0.15)',
                    fontSize:11, color:'var(--text-secondary)',
                  }}>
                    No target column for domain pre-training corpus.
                    Set your text columns to <strong>text</strong> role in the Columns tab.
                  </div>
                )}

                {/* NER: entity column selector */}
                {form.config_purpose === 'NER' && (
                  <>
                    <div className="form-grp">
                      <label className="form-lbl">
                        Entity Column
                        <span style={{ marginLeft:6, fontSize:10, color:'var(--text-muted)', fontWeight:400 }}>
                          (IOB-tagged)
                        </span>
                      </label>
                      <select
                        className="form-inp"
                        value={form.entity_column || ''}
                        onChange={e => set('entity_column', e.target.value)}
                        disabled={fc.is_locked}
                      >
                        <option value="">- Select IOB entity column -</option>
                        {columns.map(c => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                            {c.inferred_type ? ` (${c.inferred_type})` : ''}
                          </option>
                        ))}
                      </select>
                      <div className="form-hint">
                        Column containing space-separated IOB tags per token.
                        Format: <span style={{fontFamily:'var(--font-mono)', fontSize:10}}>O B-ORG I-ORG B-PER O</span>
                      </div>
                    </div>
                    <div style={{
                      padding:'10px 12px', borderRadius:'var(--radius-sm)',
                      background:'rgba(124,58,237,0.04)', border:'1px solid rgba(124,58,237,0.15)',
                      fontSize:11, color:'var(--text-secondary)',
                    }}>
                      NER training uses the TRAIN plugin with the NER template.
                      Go to <strong>Plugins</strong> → New Plugin → TRAIN → load the NER Template.
                    </div>
                  </>
                )}

                {/* Split visualiser */}
                <div className="form-grp">
                  <label className="form-lbl">Train / Val / Test Split</label>
                  <div style={{
                    height: 8, borderRadius: 4, overflow:'hidden',
                    display:'flex', marginBottom:12, background:'#F7F9FC'
                  }}>
                    {splitPct.map(s => (
                      <div key={s.key} style={{
                        width: `${form[s.key]*100}%`,
                        background: s.color,
                        transition: 'width .2s'
                      }}/>
                    ))}
                  </div>
                  <div className="flex gap12">
                    {splitPct.map(s => (
                      <div key={s.key} style={{ flex:1 }}>
                        <div className="xs" style={{ color:'#64748B', marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ width:8, height:8, borderRadius:'50%', background:s.color, display:'inline-block' }}/>
                          {s.label}
                        </div>
                        <input type="number" className="form-inp" min="0.05" max="0.95" step="0.05"
                          value={form[s.key]}
                          onChange={e => updateSplit(s.key, e.target.value)}
                          disabled={fc.is_locked}
                          style={{ fontFamily:'var(--f-mono)', fontSize:13 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-grp">
                  <label className="form-lbl" style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                    <input type="checkbox" checked={form.stratify}
                      onChange={e => set('stratify', e.target.checked)}
                      disabled={fc.is_locked}
                    />
                    Stratified split
                  </label>
                  <div className="form-hint">Maintains class distribution across train/val/test. Recommended for imbalanced datasets.</div>
                </div>
              </div>
            )}

            {/* ── Columns ── */}
            {tab === 'columns' && (
              <div>
                {columns.length === 0 ? (
                  <Alert type="info">No column profiles available. Run profiling first.</Alert>
                ) : (
                  <div className="card">
                    <div className="tbl-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Column</th>
                            <th>Type</th>
                            <th>Unique</th>
                            <th>Nulls</th>
                            <th>Target?</th>
                            <th>Role</th>
                          </tr>
                        </thead>
                        <tbody>
                          {columns.map(c => {
                            const purposeHasTarget = ['CLASSIFICATION','REGRESSION'].includes(form.config_purpose || 'CLASSIFICATION');
                            const isTarget = purposeHasTarget && c.name === form.target_column;
                            const featureCols = form.feature_columns || {};
                            const role = isTarget ? 'target' : (featureCols[c.name] || 'feature');
                            return (
                              <tr key={c.id} style={{ background: isTarget ? 'var(--or-dim)' : undefined }}>
                                <td className="bold">{c.name}</td>
                                <td><span className="badge b-gray">{c.inferred_type}</span></td>
                                <td className="td-mono">{c.unique_count?.toLocaleString() ?? '-'}</td>
                                <td className="td-mono">
                                  <span className={c.null_count > 0 ? 'c-am' : 'c-3'}>{c.null_count ?? '-'}</span>
                                </td>
                                <td>{c.is_candidate_target && <span className="badge b-or">★</span>}</td>
                                <td>
                                  {isTarget ? (
                                    <span className="badge b-or">target</span>
                                  ) : (
                                    <select className="form-inp" style={{ padding:'3px 6px', fontSize:11 }}
                                      value={role}
                                      disabled={fc.is_locked}
                                      onChange={e => {
                                        const newRole = e.target.value;
                                        set('feature_columns', { ...featureCols, [c.name]: newRole });
                                        // Auto-couple encoding to role selection
                                        const currentEnc = form.encoding_config || {};
                                        if (newRole === 'text') {
                                          // text input → always tfidf
                                          set('encoding_config', { ...currentEnc, [c.name]: 'tfidf' });
                                        } else if (newRole === 'ignore' || newRole === 'id') {
                                          // ignored columns need no encoding - clear it
                                          const { [c.name]: _, ...rest } = currentEnc;
                                          set('encoding_config', rest);
                                        }
                                        // feature role keeps whatever encoding was set
                                      }}>
                                      <option value="feature">feature</option>
                                      <option value="text">text input</option>
                                      <option value="ignore">ignore</option>
                                      <option value="id">id / key</option>
                                    </select>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Encoding ── */}
            {tab === 'encoding' && (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <Alert type="info">
                  Encoding overrides control how each column is transformed before training.
                  Leave blank to use auto-detected defaults.
                </Alert>
                {columns
                  .filter(c => c.name !== form.target_column)
                  .filter(c => {
                    const role = (form.feature_columns || {})[c.name];
                    return role !== 'ignore' && role !== 'id';
                  })
                  .map(c => {
                  const enc = (form.encoding_config || {})[c.name] || '';
                  return (
                    <div key={c.id} style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <div style={{ width:180, fontSize:13, fontFamily:'var(--f-mono)', color:'#334155', flexShrink:0 }}>
                        {c.name}
                      </div>
                      <select className="form-inp" style={{ flex:1 }}
                        value={enc}
                        disabled={fc.is_locked}
                        onChange={e => set('encoding_config', { ...(form.encoding_config||{}), [c.name]: e.target.value || undefined })}>
                        <option value="">- auto -</option>
                        <option value="tfidf">TF-IDF</option>
                        <option value="count">Count Vectorizer</option>
                        <option value="label">Label Encode</option>
                        <option value="onehot">One-Hot Encode</option>
                        <option value="ordinal">Ordinal Encode</option>
                        <option value="normalize">Normalize (0-1)</option>
                        <option value="standardize">Standardize (z-score)</option>
                        <option value="passthrough">Passthrough</option>
                        <option value="drop">Drop</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </ModalBody>

      {!fc.is_locked && form && (
        <ModalFooter>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-ghost btn-sm" onClick={() => save(false)} disabled={saving}>
            {saving ? <Spinner size={12}/> : 'Save Draft'}
          </button>
          <button className="btn btn-success btn-sm" onClick={() => save(true)} disabled={saving}>
            {saving ? <Spinner size={12}/> : '✓ Save & Approve'}
          </button>
        </ModalFooter>
      )}
      {fc.is_locked && (
        <ModalFooter>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </ModalFooter>
      )}
    </Modal>
  );
}


/* ── Datasource Versions Tab ──────────────────────────────────────────────── */
function DatasourceVersionsTab({ ds }) {
  const { data: versions, loading } = useApi(`/datasources/${ds.id}/versions`);
  if (loading) return <div className="flex" style={{justifyContent:'center',padding:48}}><Spinner size={24}/></div>;
  return (
    <div>
      <div className="card-title mb16">Version History</div>
      {(!versions || versions.length === 0) ? (
        <div className="c-sl sm">No versions recorded for this datasource.</div>
      ) : (
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{borderBottom:'1px solid var(--bdr)'}}>
              {['Version','Rows','Columns','Size','Content Hash','Created'].map(h => (
                <th key={h} className="xs c-sl" style={{textAlign:'left',padding:'6px 12px',fontWeight:600}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {versions.map(v => (
              <tr key={v.id} style={{borderBottom:'1px solid var(--bdr)'}}>
                <td style={{padding:'8px 12px'}}><span className="badge b-or">v{v.version_number}</span></td>
                <td style={{padding:'8px 12px'}} className="sm">{v.row_count?.toLocaleString() ?? '-'}</td>
                <td style={{padding:'8px 12px'}} className="sm">{v.column_count ?? '-'}</td>
                <td style={{padding:'8px 12px'}} className="sm">
                  {v.file_size_bytes ? `${(v.file_size_bytes/1024/1024).toFixed(1)} MB` : '-'}
                </td>
                <td style={{padding:'8px 12px'}} className="mono sm c-sl">{v.content_hash.slice(0,16)}…</td>
                <td style={{padding:'8px 12px'}} className="sm c-sl">
                  {v.created_at ? new Date(v.created_at).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Datasource Runs Tab ──────────────────────────────────────────────────── */
function DatasourceRunsTab({ ds }) {
  const { data: versions } = useApi(`/datasources/${ds.id}/versions`);
  const [selectedVer, setSelectedVer] = React.useState(null);
  const { data: runs, loading } = useApi(
    selectedVer ? `/datasources/${ds.id}/versions/${selectedVer}/runs` : null
  );

  React.useEffect(() => {
    if (versions?.length > 0 && !selectedVer) setSelectedVer(versions[versions.length - 1].id);
  }, [versions]);

  return (
    <div>
      <div className="flex-row mb16" style={{gap:8,alignItems:'center'}}>
        <div className="card-title">Runs by Dataset Version</div>
        {versions?.length > 0 && (
          <select className="form-inp" style={{width:'auto'}}
            value={selectedVer||''} onChange={e => setSelectedVer(e.target.value)}>
            {versions.map(v => (
              <option key={v.id} value={v.id}>
                v{v.version_number} - {v.row_count?.toLocaleString()} rows · {v.content_hash.slice(0,8)}
              </option>
            ))}
          </select>
        )}
      </div>
      {loading ? (
        <div className="flex" style={{justifyContent:'center',padding:48}}><Spinner size={24}/></div>
      ) : (!runs?.items || runs.items.length === 0) ? (
        <div className="c-sl sm">No runs found for this dataset version.</div>
      ) : (
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{borderBottom:'1px solid var(--bdr)'}}>
              {['Run','Label','Status','Model','Feature Config','Target','F1','Created'].map(h => (
                <th key={h} className="xs c-sl" style={{textAlign:'left',padding:'6px 12px',fontWeight:600}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.items.map(r => (
              <tr key={r.run_id} style={{borderBottom:'1px solid var(--bdr)'}}>
                <td style={{padding:'8px 12px'}}><span className="c-or mono">#{r.run_number}</span></td>
                <td style={{padding:'8px 12px'}} className="sm">{r.run_label || '-'}</td>
                <td style={{padding:'8px 12px'}}><span className={`badge b-${r.status==='COMPLETED'?'green':r.status==='FAILED'?'red':'slate'}`}>{r.status}</span></td>
                <td style={{padding:'8px 12px'}} className="sm">{r.model || '-'}</td>
                <td style={{padding:'8px 12px'}} className="sm">{r.feature_config || '-'}</td>
                <td style={{padding:'8px 12px'}} className="mono sm c-or">{r.target_column || '-'}</td>
                <td style={{padding:'8px 12px'}} className="sm">{r.f1_score ? `${(r.f1_score*100).toFixed(1)}%` : '-'}</td>
                <td style={{padding:'8px 12px'}} className="sm c-sl">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}